type KnowledgeGraphTranslation = Record<string, string>;

export const knowledgeGraphTranslations: Record<
  string,
  KnowledgeGraphTranslation
> = {
  en: {
    title: 'Knowledge graph',
    loading: 'Loading knowledge graph…',
    loadingDescription:
      'Loading generated entities and relationships. Large graphs may take a moment.',
    searchPlaceholder: 'Search entities or descriptions',
    filterByType: 'Filter by entity type',
    entitiesCount: 'Showing {{visible}} of {{total}} entities',
    relationshipsCount: 'Showing {{visible}} of {{total}} relationships',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    fitView: 'Fit to view',
    switchTo3D: 'Switch to 3D view',
    switchTo2D: 'Switch to 2D view',
    reset3DView: 'Reset 3D view',
    enterFullscreen: 'Enter full screen',
    exitFullscreen: 'Exit full screen',
    optimizingLayout: 'Optimizing graph layout…',
    canvasHint: 'Drag to pan · Scroll to zoom · Click an entity for details',
    canvasHint3D:
      'Drag to orbit · Scroll to zoom · Click an entity for details',
    refresh: 'Refresh',
    moreActions: 'More actions',
    deleteGraph: 'Delete knowledge graph',
    noSearchResults: 'No entity matches “{{query}}”.',
    noFilterResults: 'No entities match the selected types.',
    loadError: 'Unable to load the knowledge graph',
    loadErrorDescription: 'Check your connection and try again.',
    retry: 'Retry',
    emptyTitle: 'No knowledge graph yet',
    emptyDescription:
      'Enable knowledge graph extraction in configuration, then parse or reparse documents to generate entity relationships.',
    enabledEmptyDescription:
      'GraphRAG is enabled, but the existing documents have not produced graph data yet. Reparse them and refresh this page after processing finishes.',
    configureGraphRag: 'Configure GraphRAG',
    viewDocuments: 'View documents',
    entityDetails: 'Entity details',
    relationshipDetails: 'Relationship details',
    entityType: 'Entity type',
    description: 'Description',
    communities: 'Communities',
    sourceEntity: 'Source entity',
    targetEntity: 'Target entity',
    weight: 'Weight',
    sourceIds: 'Source IDs',
    deleteConfirmTitle: 'Delete this knowledge graph?',
    deleteConfirmDescription:
      'All generated entities and relationships in this knowledge base will be removed. Documents are not deleted.',
  },
  zh: {
    title: '知识图谱',
    loading: '正在加载知识图谱…',
    loadingDescription: '正在加载实体和关系数据，较大的图谱可能需要一点时间。',
    searchPlaceholder: '搜索实体名称或描述',
    filterByType: '按实体类型筛选',
    entitiesCount: '展示 {{visible}} / 共 {{total}} 个实体',
    relationshipsCount: '展示 {{visible}} / 共 {{total}} 条关系',
    zoomIn: '放大',
    zoomOut: '缩小',
    fitView: '适应画布',
    switchTo3D: '切换至 3D 视图',
    switchTo2D: '切换至 2D 视图',
    reset3DView: '重置 3D 视角',
    enterFullscreen: '进入全屏',
    exitFullscreen: '退出全屏',
    optimizingLayout: '正在优化图谱布局…',
    canvasHint: '拖动画布 · 滚轮缩放 · 点击实体查看详情',
    canvasHint3D: '拖动旋转视角 · 滚轮缩放 · 点击实体查看详情',
    refresh: '刷新',
    moreActions: '更多操作',
    deleteGraph: '删除知识图谱',
    noSearchResults: '未找到与“{{query}}”匹配的实体。',
    noFilterResults: '没有符合所选类型的实体。',
    loadError: '知识图谱加载失败',
    loadErrorDescription: '请检查网络连接后重试。',
    retry: '重试',
    emptyTitle: '尚未生成知识图谱',
    emptyDescription:
      '请在知识库配置中开启知识图谱提取，然后解析或重新解析文档以生成实体关系。',
    enabledEmptyDescription:
      'GraphRAG 已开启，但现有文档尚未生成图谱数据。请在文档列表重新解析，任务完成后刷新本页。',
    configureGraphRag: '配置 GraphRAG',
    viewDocuments: '查看文档',
    entityDetails: '实体详情',
    relationshipDetails: '关系详情',
    entityType: '实体类型',
    description: '描述',
    communities: '所属社区',
    sourceEntity: '起始实体',
    targetEntity: '目标实体',
    weight: '权重',
    sourceIds: '来源 ID',
    deleteConfirmTitle: '确定删除此知识图谱吗？',
    deleteConfirmDescription:
      '该知识库中已生成的全部实体和关系将被删除，原始文档不会被删除。',
  },
  'zh-TRADITIONAL': {
    title: '知識圖譜',
    searchPlaceholder: '搜尋實體名稱或描述',
    filterByType: '按實體類型篩選',
    entitiesCount: '顯示 {{visible}} / 共 {{total}} 個實體',
    relationshipsCount: '顯示 {{visible}} / 共 {{total}} 條關係',
    zoomIn: '放大',
    zoomOut: '縮小',
    fitView: '適應畫布',
    switchTo3D: '切換至 3D 檢視',
    switchTo2D: '切換至 2D 檢視',
    reset3DView: '重設 3D 視角',
    enterFullscreen: '進入全螢幕',
    exitFullscreen: '退出全螢幕',
    optimizingLayout: '正在最佳化圖譜佈局…',
    canvasHint: '拖動畫布 · 滾輪縮放 · 點擊實體查看詳情',
    canvasHint3D: '拖動旋轉視角 · 滾輪縮放 · 點擊實體查看詳情',
    refresh: '重新整理',
    moreActions: '更多操作',
    deleteGraph: '刪除知識圖譜',
    noSearchResults: '找不到與「{{query}}」相符的實體。',
    noFilterResults: '沒有符合所選類型的實體。',
    loadError: '知識圖譜載入失敗',
    loadErrorDescription: '請檢查網路連線後重試。',
    retry: '重試',
    emptyTitle: '尚未產生知識圖譜',
    emptyDescription:
      '請在知識庫設定中開啟知識圖譜擷取，然後解析或重新解析文件。',
    enabledEmptyDescription:
      'GraphRAG 已啟用，但現有文件尚未產生圖譜資料。請重新解析文件，任務完成後重新整理本頁。',
    configureGraphRag: '設定 GraphRAG',
    viewDocuments: '查看文件',
    entityDetails: '實體詳情',
    relationshipDetails: '關係詳情',
    entityType: '實體類型',
    description: '描述',
    communities: '所屬社群',
    sourceEntity: '起始實體',
    targetEntity: '目標實體',
    weight: '權重',
    sourceIds: '來源 ID',
    deleteConfirmTitle: '確定刪除此知識圖譜嗎？',
    deleteConfirmDescription:
      '知識庫中已產生的所有實體與關係將被刪除，原始文件不會被刪除。',
  },
  id: {
    title: 'Graf pengetahuan',
    searchPlaceholder: 'Cari entitas atau deskripsi',
    filterByType: 'Filter berdasarkan jenis entitas',
    entitiesCount: '{{visible}} / {{total}} entitas',
    relationshipsCount: '{{visible}} / {{total}} relasi',
    zoomIn: 'Perbesar',
    zoomOut: 'Perkecil',
    fitView: 'Sesuaikan tampilan',
    refresh: 'Muat ulang',
    moreActions: 'Tindakan lainnya',
    deleteGraph: 'Hapus graf pengetahuan',
    noSearchResults: 'Tidak ada entitas yang cocok dengan “{{query}}”.',
    noFilterResults: 'Tidak ada entitas yang cocok dengan jenis terpilih.',
    loadError: 'Graf pengetahuan tidak dapat dimuat',
    loadErrorDescription: 'Periksa koneksi Anda lalu coba lagi.',
    retry: 'Coba lagi',
    emptyTitle: 'Belum ada graf pengetahuan',
    emptyDescription:
      'Aktifkan ekstraksi graf pengetahuan, lalu parse atau parse ulang dokumen.',
    configureGraphRag: 'Konfigurasi GraphRAG',
    viewDocuments: 'Lihat dokumen',
    entityDetails: 'Detail entitas',
    relationshipDetails: 'Detail relasi',
    entityType: 'Jenis entitas',
    description: 'Deskripsi',
    communities: 'Komunitas',
    sourceEntity: 'Entitas sumber',
    targetEntity: 'Entitas target',
    weight: 'Bobot',
    sourceIds: 'ID sumber',
    deleteConfirmTitle: 'Hapus graf pengetahuan ini?',
    deleteConfirmDescription:
      'Semua entitas dan relasi yang dihasilkan akan dihapus. Dokumen tidak akan dihapus.',
  },
  ja: {
    title: 'ナレッジグラフ',
    searchPlaceholder: 'エンティティ名または説明を検索',
    filterByType: 'エンティティ種別で絞り込み',
    entitiesCount: '{{visible}} / {{total}} エンティティ',
    relationshipsCount: '{{visible}} / {{total}} 関係',
    zoomIn: '拡大',
    zoomOut: '縮小',
    fitView: '画面に合わせる',
    refresh: '更新',
    moreActions: 'その他の操作',
    deleteGraph: 'ナレッジグラフを削除',
    noSearchResults: '「{{query}}」に一致するエンティティはありません。',
    noFilterResults: '選択した種別に一致するエンティティはありません。',
    loadError: 'ナレッジグラフを読み込めません',
    loadErrorDescription: '接続を確認して再試行してください。',
    retry: '再試行',
    emptyTitle: 'ナレッジグラフはまだありません',
    emptyDescription:
      'ナレッジグラフ抽出を有効にし、文書を解析または再解析してください。',
    configureGraphRag: 'GraphRAG を設定',
    viewDocuments: '文書を表示',
    entityDetails: 'エンティティ詳細',
    relationshipDetails: '関係の詳細',
    entityType: 'エンティティ種別',
    description: '説明',
    communities: 'コミュニティ',
    sourceEntity: '始点エンティティ',
    targetEntity: '終点エンティティ',
    weight: '重み',
    sourceIds: 'ソース ID',
    deleteConfirmTitle: 'このナレッジグラフを削除しますか？',
    deleteConfirmDescription:
      '生成されたすべてのエンティティと関係が削除されます。文書は削除されません。',
  },
  es: {
    title: 'Grafo de conocimiento',
    searchPlaceholder: 'Buscar entidades o descripciones',
    filterByType: 'Filtrar por tipo de entidad',
    entitiesCount: '{{visible}} / {{total}} entidades',
    relationshipsCount: '{{visible}} / {{total}} relaciones',
    zoomIn: 'Acercar',
    zoomOut: 'Alejar',
    fitView: 'Ajustar a la vista',
    refresh: 'Actualizar',
    moreActions: 'Más acciones',
    deleteGraph: 'Eliminar grafo de conocimiento',
    noSearchResults: 'Ninguna entidad coincide con “{{query}}”.',
    noFilterResults: 'Ninguna entidad coincide con los tipos seleccionados.',
    loadError: 'No se pudo cargar el grafo de conocimiento',
    loadErrorDescription: 'Comprueba la conexión e inténtalo de nuevo.',
    retry: 'Reintentar',
    emptyTitle: 'Aún no hay grafo de conocimiento',
    emptyDescription:
      'Activa la extracción del grafo y analiza o vuelve a analizar los documentos.',
    configureGraphRag: 'Configurar GraphRAG',
    viewDocuments: 'Ver documentos',
    entityDetails: 'Detalles de la entidad',
    relationshipDetails: 'Detalles de la relación',
    entityType: 'Tipo de entidad',
    description: 'Descripción',
    communities: 'Comunidades',
    sourceEntity: 'Entidad de origen',
    targetEntity: 'Entidad de destino',
    weight: 'Peso',
    sourceIds: 'ID de origen',
    deleteConfirmTitle: '¿Eliminar este grafo de conocimiento?',
    deleteConfirmDescription:
      'Se eliminarán todas las entidades y relaciones generadas. Los documentos no se eliminarán.',
  },
  vi: {
    title: 'Đồ thị tri thức',
    searchPlaceholder: 'Tìm thực thể hoặc mô tả',
    filterByType: 'Lọc theo loại thực thể',
    entitiesCount: '{{visible}} / {{total}} thực thể',
    relationshipsCount: '{{visible}} / {{total}} quan hệ',
    zoomIn: 'Phóng to',
    zoomOut: 'Thu nhỏ',
    fitView: 'Vừa khung nhìn',
    refresh: 'Làm mới',
    moreActions: 'Thao tác khác',
    deleteGraph: 'Xóa đồ thị tri thức',
    noSearchResults: 'Không có thực thể khớp với “{{query}}”.',
    noFilterResults: 'Không có thực thể khớp với loại đã chọn.',
    loadError: 'Không thể tải đồ thị tri thức',
    loadErrorDescription: 'Hãy kiểm tra kết nối và thử lại.',
    retry: 'Thử lại',
    emptyTitle: 'Chưa có đồ thị tri thức',
    emptyDescription:
      'Bật trích xuất đồ thị tri thức, sau đó phân tích hoặc phân tích lại tài liệu.',
    configureGraphRag: 'Cấu hình GraphRAG',
    viewDocuments: 'Xem tài liệu',
    entityDetails: 'Chi tiết thực thể',
    relationshipDetails: 'Chi tiết quan hệ',
    entityType: 'Loại thực thể',
    description: 'Mô tả',
    communities: 'Cộng đồng',
    sourceEntity: 'Thực thể nguồn',
    targetEntity: 'Thực thể đích',
    weight: 'Trọng số',
    sourceIds: 'ID nguồn',
    deleteConfirmTitle: 'Xóa đồ thị tri thức này?',
    deleteConfirmDescription:
      'Tất cả thực thể và quan hệ đã tạo sẽ bị xóa. Tài liệu không bị xóa.',
  },
  ru: {
    title: 'Граф знаний',
    searchPlaceholder: 'Поиск сущностей или описаний',
    filterByType: 'Фильтр по типу сущности',
    entitiesCount: '{{visible}} / {{total}} сущностей',
    relationshipsCount: '{{visible}} / {{total}} связей',
    zoomIn: 'Увеличить',
    zoomOut: 'Уменьшить',
    fitView: 'Вписать в экран',
    refresh: 'Обновить',
    moreActions: 'Другие действия',
    deleteGraph: 'Удалить граф знаний',
    noSearchResults: 'Нет сущностей, соответствующих «{{query}}».',
    noFilterResults: 'Нет сущностей выбранных типов.',
    loadError: 'Не удалось загрузить граф знаний',
    loadErrorDescription: 'Проверьте подключение и повторите попытку.',
    retry: 'Повторить',
    emptyTitle: 'Граф знаний еще не создан',
    emptyDescription:
      'Включите извлечение графа знаний, затем обработайте документы повторно.',
    configureGraphRag: 'Настроить GraphRAG',
    viewDocuments: 'Открыть документы',
    entityDetails: 'Сведения о сущности',
    relationshipDetails: 'Сведения о связи',
    entityType: 'Тип сущности',
    description: 'Описание',
    communities: 'Сообщества',
    sourceEntity: 'Исходная сущность',
    targetEntity: 'Целевая сущность',
    weight: 'Вес',
    sourceIds: 'ID источников',
    deleteConfirmTitle: 'Удалить этот граф знаний?',
    deleteConfirmDescription:
      'Все созданные сущности и связи будут удалены. Документы останутся без изменений.',
  },
  'pt-BR': {
    title: 'Grafo de conhecimento',
    searchPlaceholder: 'Pesquisar entidades ou descrições',
    filterByType: 'Filtrar por tipo de entidade',
    entitiesCount: '{{visible}} / {{total}} entidades',
    relationshipsCount: '{{visible}} / {{total}} relações',
    zoomIn: 'Ampliar',
    zoomOut: 'Reduzir',
    fitView: 'Ajustar à tela',
    refresh: 'Atualizar',
    moreActions: 'Mais ações',
    deleteGraph: 'Excluir grafo de conhecimento',
    noSearchResults: 'Nenhuma entidade corresponde a “{{query}}”.',
    noFilterResults: 'Nenhuma entidade corresponde aos tipos selecionados.',
    loadError: 'Não foi possível carregar o grafo',
    loadErrorDescription: 'Verifique sua conexão e tente novamente.',
    retry: 'Tentar novamente',
    emptyTitle: 'Ainda não há grafo de conhecimento',
    emptyDescription:
      'Ative a extração do grafo e processe ou reprocesse os documentos.',
    configureGraphRag: 'Configurar GraphRAG',
    viewDocuments: 'Ver documentos',
    entityDetails: 'Detalhes da entidade',
    relationshipDetails: 'Detalhes da relação',
    entityType: 'Tipo de entidade',
    description: 'Descrição',
    communities: 'Comunidades',
    sourceEntity: 'Entidade de origem',
    targetEntity: 'Entidade de destino',
    weight: 'Peso',
    sourceIds: 'IDs de origem',
    deleteConfirmTitle: 'Excluir este grafo de conhecimento?',
    deleteConfirmDescription:
      'Todas as entidades e relações geradas serão excluídas. Os documentos serão mantidos.',
  },
  de: {
    title: 'Wissensgraph',
    searchPlaceholder: 'Entitäten oder Beschreibungen suchen',
    filterByType: 'Nach Entitätstyp filtern',
    entitiesCount: '{{visible}} / {{total}} Entitäten',
    relationshipsCount: '{{visible}} / {{total}} Beziehungen',
    zoomIn: 'Vergrößern',
    zoomOut: 'Verkleinern',
    fitView: 'Ansicht einpassen',
    refresh: 'Aktualisieren',
    moreActions: 'Weitere Aktionen',
    deleteGraph: 'Wissensgraph löschen',
    noSearchResults: 'Keine Entität entspricht „{{query}}“.',
    noFilterResults: 'Keine Entität entspricht den ausgewählten Typen.',
    loadError: 'Wissensgraph konnte nicht geladen werden',
    loadErrorDescription:
      'Prüfen Sie die Verbindung und versuchen Sie es erneut.',
    retry: 'Erneut versuchen',
    emptyTitle: 'Noch kein Wissensgraph vorhanden',
    emptyDescription:
      'Aktivieren Sie die Graph-Extraktion und analysieren Sie die Dokumente erneut.',
    configureGraphRag: 'GraphRAG konfigurieren',
    viewDocuments: 'Dokumente anzeigen',
    entityDetails: 'Entitätsdetails',
    relationshipDetails: 'Beziehungsdetails',
    entityType: 'Entitätstyp',
    description: 'Beschreibung',
    communities: 'Communities',
    sourceEntity: 'Ausgangsentität',
    targetEntity: 'Zielentität',
    weight: 'Gewicht',
    sourceIds: 'Quell-IDs',
    deleteConfirmTitle: 'Diesen Wissensgraph löschen?',
    deleteConfirmDescription:
      'Alle erzeugten Entitäten und Beziehungen werden entfernt. Dokumente werden nicht gelöscht.',
  },
  fr: {
    title: 'Graphe de connaissances',
    searchPlaceholder: 'Rechercher des entités ou descriptions',
    filterByType: "Filtrer par type d'entité",
    entitiesCount: '{{visible}} / {{total}} entités',
    relationshipsCount: '{{visible}} / {{total}} relations',
    zoomIn: 'Zoom avant',
    zoomOut: 'Zoom arrière',
    fitView: 'Ajuster à la vue',
    refresh: 'Actualiser',
    moreActions: "Plus d'actions",
    deleteGraph: 'Supprimer le graphe de connaissances',
    noSearchResults: 'Aucune entité ne correspond à « {{query}} ».',
    noFilterResults: 'Aucune entité ne correspond aux types sélectionnés.',
    loadError: 'Impossible de charger le graphe',
    loadErrorDescription: 'Vérifiez votre connexion puis réessayez.',
    retry: 'Réessayer',
    emptyTitle: 'Aucun graphe de connaissances',
    emptyDescription:
      "Activez l'extraction du graphe, puis analysez ou réanalysez les documents.",
    configureGraphRag: 'Configurer GraphRAG',
    viewDocuments: 'Voir les documents',
    entityDetails: "Détails de l'entité",
    relationshipDetails: 'Détails de la relation',
    entityType: "Type d'entité",
    description: 'Description',
    communities: 'Communautés',
    sourceEntity: "Entité d'origine",
    targetEntity: 'Entité cible',
    weight: 'Poids',
    sourceIds: 'ID source',
    deleteConfirmTitle: 'Supprimer ce graphe de connaissances ?',
    deleteConfirmDescription:
      'Toutes les entités et relations générées seront supprimées. Les documents seront conservés.',
  },
};

export const withKnowledgeGraphTranslation = <
  T extends { translation: Record<string, unknown> },
>(
  resource: T,
  language: string,
) => ({
  ...resource,
  translation: {
    ...resource.translation,
    knowledgeGraphPage: {
      ...knowledgeGraphTranslations.en,
      ...(knowledgeGraphTranslations[language] ?? {}),
    },
  },
});
